
import React, { useState, useEffect, useRef } from "react";
import { Worker, Viewer, SpecialZoomLevel } from "@react-pdf-viewer/core";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import { searchPlugin } from "@react-pdf-viewer/search";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import * as pdfjsLib from "pdfjs-dist";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/toolbar/lib/styles/index.css";
import "@react-pdf-viewer/search/lib/styles/index.css";
import "@react-pdf-viewer/page-navigation/lib/styles/index.css";

export default function PDFViewer({ file, initialPage = 1, zoomLevel }) {
  const [loading, setLoading] = useState(true);
  const [fileUrl, setFileUrl] = useState(null);
  const viewerRef = useRef(null);
  const [currentZoom, setCurrentZoom] = useState(SpecialZoomLevel.PageWidth);

  useEffect(() => {
    if (file) {
      setLoading(true);
      // Handle both string URLs and Blob/File objects
      const url = file instanceof Blob ? URL.createObjectURL(file) : file;
      setFileUrl(url);
    } else {
      setFileUrl(null);
    }
    return () => {
      if (fileUrl && fileUrl.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [file]);

  // Effect to handle sidebar width changes
  useEffect(() => {
    // When the sidebar width changes, trigger a resize event
    // This will cause the PDF viewer to recalculate the page width
    if (viewerRef.current) {
      window.dispatchEvent(new Event('resize'));
    }
  }, [zoomLevel]);

  // Create search plugin instance with custom options
  const searchPluginInstance = searchPlugin();

  // Create a page navigation plugin instance
  const pageNavigationPluginInstance = pageNavigationPlugin();

  // Create a toolbar plugin instance
  const toolbarPluginInstance = toolbarPlugin();
  const { renderDefaultToolbar, Toolbar } = toolbarPluginInstance;

  // Define a transform function to remove specific toolbar items
  const transform = (slot) => ({
    ...slot,
    // Remove Print button
    Print: () => <></>,
    PrintMenuItem: () => <></>,
    // Remove Fullscreen button
    EnterFullScreen: () => <></>,
    EnterFullScreenMenuItem: () => <></>,
    // Remove Dark Mode button
    SwitchTheme: () => <></>,
    SwitchThemeMenuItem: () => <></>,
    // Remove Open File button
    Open: () => <></>,
    OpenMenuItem: () => <></>,
    // Remove More Actions button
    MoreActionsPopover: () => <></>,
  });

  // Fullscreen functionality removed

  if (!file) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
        <p className="text-gray-500">No PDF file provided</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto">
      <div className="w-full bg-white rounded-lg shadow-lg">
        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        )}
        
        {fileUrl && (
          <Worker workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`}>
            <div className="rpv-core__viewer" style={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
              {/* Add CSS to hide search options */}
              <style>
                {`
                  /* Hide Match Case and Whole Words checkboxes */
                  .rpv-search__popover-label-checkbox {
                    display: none !important;
                  }
                  
                  /* Hide More button */
                  .rpv-toolbar__more-actions-popover {
                    display: none !important;
                  }
                `}
              </style>
              <div style={{ 
                alignItems: "center",
                backgroundColor: "#eeeeee",
                borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                display: "flex",
                padding: "0.25rem",
                justifyContent: "space-between"
              }}>
                <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>
              </div>
              <div style={{ flex: 1, overflow: "hidden" }} ref={viewerRef}>
                <Viewer 
                  fileUrl={fileUrl} 
                  plugins={[toolbarPluginInstance, searchPluginInstance, pageNavigationPluginInstance]}
                  onDocumentLoad={() => setLoading(false)}
                  initialPage={initialPage - 1}
                  defaultScale={SpecialZoomLevel.PageWidth}
                />
              </div>
            </div>
          </Worker>
        )}
      </div>
    </div>
  );
}